import {
  GET_TAX_SETTINGS,
  GET_ACCOUNT_TAX_SETTINGS,
  UPDATE_DEFAULT_TAX_RATE,
  TOGGLE_TAXABLE,
  UPDATE_TAX_RATE,
  APPLY_DEFAULT_RATE_TO_ALL,
  UPDATE_ACCOUNT_TAX_RATE,
  TOGGLE_ACCOUNT_TAXABLE
} from '../actionTypes';
import { getAccessToken } from '../authenticateActions';
import { getAxiosInstance } from '../../util/service';
import { store } from '../../store/configureStore';
import { getCompanyId } from '../../util/customMethods';
import { moduleTypes } from '../../util/constants';
import { getChargeList } from './ratePlanActions';
// Toggle taxable for an account charge
export const toggleAccountTaxable = (chargeListId) => {
  return {
    type: TOGGLE_ACCOUNT_TAXABLE,
    payload: chargeListId
  };
};

// Update tax rate for an account charge
export const updateAccountTaxRate = (chargeListId, rate) => {
  return {
    type: UPDATE_ACCOUNT_TAX_RATE,
    payload: { chargeListId, rate }
  };
};

// Get tax settings for company
export const getTaxSettings = () => {
  return async (dispatch) => {
    try {
      // First, make sure we have the latest charge list
      await dispatch(getChargeList());
      
      // Get the charge list from the store
      const { chargeList } = store.getState().ratePlanList;
      
      try {
        // Now try to get tax settings
        const _url = `tax-settings/company?companyId=${getCompanyId()}&access_token=${getAccessToken()}`;
        const response = await getAxiosInstance(moduleTypes.ADMIN).get(_url);
        
        if (response.data.statusCode === 200) {
          // Combine charge list with tax settings
          const taxData = response.data.resp || {};
          
          // Map tax settings to charge list
          const combinedChargeList = chargeList.map(charge => {
            const taxSetting = (taxData.chargeTaxSettings || []).find(
              setting => setting.chargeListId === charge.chargeListId
            );
            
            return {
              ...charge,
              isTaxable: taxSetting ? taxSetting.isTaxable : false,
              taxRate: taxSetting ? taxSetting.taxRate : 0
            };
          });
          
          dispatch({
            type: GET_TAX_SETTINGS,
            payload: {
              companyTaxConfig: taxData.companyTaxConfig || { defaultTaxRate: 0 },
              chargeTaxSettings: combinedChargeList
            }
          });
          
          return { statusCode: 200, statusMsg: 'Tax settings loaded successfully' };
        } else {
          // API returned an error but we still have charge list
          const combinedChargeList = chargeList.map(charge => ({
            ...charge,
            isTaxable: false,
            taxRate: 0
          }));
          
          dispatch({
            type: GET_TAX_SETTINGS,
            payload: {
              companyTaxConfig: { defaultTaxRate: 0 },
              chargeTaxSettings: combinedChargeList
            }
          });
          
          return { 
            statusCode: response.data.statusCode, 
            statusMsg: response.data.statusMsg || 'Failed to load tax settings' 
          };
        }
      } catch (apiError) {
        // API endpoint might be missing, but we still have charge list
        console.error('Error fetching tax settings:', apiError);
        
        const combinedChargeList = chargeList.map(charge => ({
          ...charge,
          isTaxable: false,
          taxRate: 0
        }));
        
        dispatch({
          type: GET_TAX_SETTINGS,
          payload: {
            companyTaxConfig: { defaultTaxRate: 0 },
            chargeTaxSettings: combinedChargeList
          }
        });
        
        return { 
          statusCode: apiError.response?.status || 500, 
          statusMsg: 'Tax API not available. Using charge list with default tax values.' 
        };
      }
    } catch (ex) {
      console.error('Error getting charge list:', ex);
      return { statusCode: 500, statusMsg: 'An unexpected error occurred loading charge list.' };
    }
  };
};

// Get tax settings for account
export const getAccountTaxSettings = (accountId) => {
  return async (dispatch) => {
    try {
      // First, make sure we have the latest charge list
      await dispatch(getChargeList());
      
      // Get the charge list from the store
      const { chargeList } = store.getState().ratePlanList;
      
      try {
        // Now try to get account tax settings
        const _url = `tax-settings/account?accountId=${accountId}&companyId=${getCompanyId()}&access_token=${getAccessToken()}`;
        const response = await getAxiosInstance(moduleTypes.ADMIN).get(_url);
        
        if (response.data.statusCode === 200) {
          // Combine charge list with tax settings
          const taxData = response.data.resp || {};
          
          // Map tax settings to charge list
          const accountTaxSettings = chargeList.map(charge => {
            const taxSetting = (taxData.accountTaxSettings || []).find(
              setting => setting.chargeListId === charge.chargeListId
            );
            
            return {
              ...charge,
              isTaxable: taxSetting ? taxSetting.isTaxable : false,
              taxRate: taxSetting ? taxSetting.taxRate : 0
            };
          });
          
          dispatch({
            type: GET_ACCOUNT_TAX_SETTINGS,
            payload: {
              accountTaxSettings
            }
          });
          
          return { statusCode: 200, statusMsg: 'Account tax settings loaded successfully',res:accountTaxSettings };
        } else {
          // API returned an error but we still have charge list
          const accountTaxSettings = chargeList.map(charge => ({
            ...charge,
            isTaxable: false,
            taxRate: 0
          }));
          
          dispatch({
            type: GET_ACCOUNT_TAX_SETTINGS,
            payload: {
              accountTaxSettings
            }
          });
          
          return { 
            statusCode: response.data.statusCode, 
            statusMsg: response.data.statusMsg || 'Failed to load account tax settings' 
          };
        }
      } catch (apiError) {
        // API endpoint might be missing, but we still have charge list
        console.error('Error fetching account tax settings:', apiError);
        
        const accountTaxSettings = chargeList.map(charge => ({
          ...charge,
          isTaxable: false,
          taxRate: 0
        }));
        
        dispatch({
          type: GET_ACCOUNT_TAX_SETTINGS,
          payload: {
            accountTaxSettings
          }
        });
        
        return { 
          statusCode: apiError.response?.status || 500, 
          statusMsg: 'Account tax API not available. Using charge list with default tax values.' 
        };
      }
    } catch (ex) {
      console.error('Error getting charge list:', ex);
      return { statusCode: 500, statusMsg: 'An unexpected error occurred loading charge list.' };
    }
  };
};

// Update default tax rate
export const updateDefaultTaxRate = (rate) => {
  return {
    type: UPDATE_DEFAULT_TAX_RATE,
    payload: rate
  };
};

// Toggle taxable for a charge
export const toggleTaxable = (chargeListId) => {
  return {
    type: TOGGLE_TAXABLE,
    payload: chargeListId
  };
};

// Update tax rate for a charge
export const updateTaxRate = (chargeListId, rate) => {
  return {
    type: UPDATE_TAX_RATE,
    payload: { chargeListId, rate }
  };
};

// Apply default rate to all taxable charges
export const applyDefaultRateToAll = () => {
  return {
    type: APPLY_DEFAULT_RATE_TO_ALL
  };
};

// Save tax settings
export const saveTaxSettings = (settings) => {
  return async (dispatch) => {
    try {
      // Extract just the tax settings from the combined data
      const taxSettings = {
        companyTaxConfig: {
          defaultTaxRate: settings.companyTaxConfig.defaultTaxRate
        },
        chargeTaxSettings: settings.chargeTaxSettings.map(charge => ({
          chargeListId: charge.chargeListId,
          isTaxable: charge.isTaxable,
          taxRate: charge.taxRate
        }))
      };
      
      try {
        const _url = `tax-settings/company?companyId=${getCompanyId()}&access_token=${getAccessToken()}`;
        const response = await getAxiosInstance(moduleTypes.ADMIN).put(_url, taxSettings);
        
        if (response.data.statusCode === 200) {
          return { statusCode: 200, statusMsg: response.data.statusMsg || 'Tax settings saved successfully' };
        } else {
          return { statusCode: response.data.statusCode, statusMsg: response.data.statusMsg || 'Failed to save tax settings' };
        }
      } catch (apiError) {
        console.error('Error saving tax settings:', apiError);
        return { 
          statusCode: apiError.response?.status || 500, 
          statusMsg: 'Tax API not available. Unable to save tax settings.' 
        };
      }
    } catch (ex) {
      console.error('Error preparing tax settings:', ex);
      return { statusCode: 500, statusMsg: 'An unexpected error occurred preparing tax settings.' };
    }
  };
};

// Save account tax settings
export const saveAccountTaxSettings = (settings, accountId) => {
  return async (dispatch) => {
    try {
      // Extract just the tax settings from the combined data
      const taxSettings = {
        accountTaxSettings: settings.map(charge => ({
          chargeListId: charge.chargeListId,
          isTaxable: charge.isTaxable,
          taxRate: charge.taxRate
        }))
      };
      
      try {
        const _url = `tax-settings/account?accountId=${accountId}&companyId=${getCompanyId()}&access_token=${getAccessToken()}`;
        const response = await getAxiosInstance(moduleTypes.ADMIN).put(_url, taxSettings);
        
        if (response.data.statusCode === 200) {
          return { statusCode: 200, statusMsg: response.data.statusMsg || 'Account tax settings saved successfully' };
        } else {
          return { statusCode: response.data.statusCode, statusMsg: response.data.statusMsg || 'Failed to save account tax settings' };
        }
      } catch (apiError) {
        console.error('Error saving account tax settings:', apiError);
        return { 
          statusCode: apiError.response?.status || 500, 
          statusMsg: 'Account tax API not available. Unable to save account tax settings.' 
        };
      }
    } catch (ex) {
      console.error('Error preparing account tax settings:', ex);
      return { statusCode: 500, statusMsg: 'An unexpected error occurred preparing account tax settings.' };
    }
  };
};